import { useRef, useState } from 'react'
import { ClipboardPaste, Paperclip, X, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { type LangCode } from '@/lib/baba-api'
import { EXAMPLE_PHRASES } from './constants'
import { FILE_ACCEPT } from './fileKind'
import type { RestoredMedia, TranslatorTab } from './types'
import { AttachedFilePreview, DocumentBadge } from './subcomponents'
import { FileTranslationProgressView } from './FileTranslationProgressView'
import type { FileJobState } from './fileTranslationProgress'
import { WsButtonPrimary, WsButtonSecondary } from './workspace'

export interface TranslatorSourcePaneProps {
  tab: TranslatorTab
  sourceLang: LangCode
  sourceRtl: boolean
  inputText: string
  onInputText: (v: string) => void
  charLimit: number
  isTranslating: boolean
  onCancelFileJob: () => void
  onClear: () => void
  onPasteFromClipboard: () => void
  onAttach: (file: File) => void
  onExampleClick: (example: string) => void
  runTranslate: () => void
  /** Primary action lives in the pane footer so it sits where the eye already is. */
  canTranslate?: boolean
  hasCompletedTranslation?: boolean
  onCancelTranslate?: () => void
  /** The one picked file, whatever its kind. */
  file: File | null
  /** Object URL, images only. */
  filePreviewUrl: string
  pdfPageCount: number | undefined
  restoredMedia?: RestoredMedia | null
  isAuthed: boolean
  isPro: boolean
  onLimitAction?: () => void
  fileJob: FileJobState | null
  /** When the current run started; the file progress view keeps its own clock. */
  translationStartMs: number | null
  hebrewFacts?: string[]
  factIndex?: number
}

export function TranslatorSourcePane({
  tab,
  sourceLang,
  sourceRtl,
  inputText,
  onInputText,
  charLimit,
  isTranslating,
  onCancelFileJob,
  onClear,
  onPasteFromClipboard,
  onAttach,
  canTranslate = false,
  hasCompletedTranslation = false,
  onCancelTranslate,
  onExampleClick,
  runTranslate,
  file,
  filePreviewUrl,
  pdfPageCount,
  restoredMedia,
  isAuthed,
  isPro,
  onLimitAction,
  fileJob,
  translationStartMs,
  hebrewFacts,
  factIndex = 0,
}: TranslatorSourcePaneProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const attachInputRef = useRef<HTMLInputElement | null>(null)
  const [dragging, setDragging] = useState(false)
  const clearAndRefocus = () => {
    onClear()
    requestAnimationFrame(() => textareaRef.current?.focus())
  }

  const overLimit = inputText.length >= charLimit
  const hasSomethingToClear = tab === 'file' ? !!file || !!restoredMedia : !!inputText
  const canQuickClear = Boolean((tab === 'file' ? file || restoredMedia : inputText) && !isTranslating)
  const primaryLabel =
    tab === 'file'
      ? hasCompletedTranslation
        ? 'Translate Again'
        : 'Translate File'
      : hasCompletedTranslation
        ? 'Translate Again'
        : 'Translate'

  return (
    <div
      className="flex flex-1 min-w-0 flex-col h-full min-h-0"
      onDragOver={(e) => {
        e.preventDefault()
        setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        const f = e.dataTransfer.files?.[0]
        if (f) onAttach(f)
      }}
      style={{ boxShadow: dragging ? 'inset 0 0 0 2px var(--accent)' : undefined, background: dragging ? 'var(--accent-soft)' : undefined }}
    >
      <div className="flex items-center justify-between gap-2 h-[30px] shrink-0 px-[18px] pt-2.5 min-w-0 overflow-hidden">
        <span className="font-mono truncate min-w-0" style={{ fontSize: 11, letterSpacing: '0.06em', color: 'var(--muted-2)' }}>
          SOURCE · {sourceLang.toUpperCase()}
        </span>
        {/* The character counter + quick clear on top */}
        <div className="flex shrink-0 items-center gap-2">
          {canQuickClear && (
            <button
              type="button"
              onClick={clearAndRefocus}
              title="Clear input (Esc)"
              aria-label="Clear input text"
              className="hidden md:inline-flex items-center gap-1 h-[22px] px-2 rounded text-[11px] font-medium transition-colors duration-150 cursor-pointer text-[var(--muted-2)] hover:text-red-500 hover:bg-red-50/60 dark:hover:bg-red-950/25"
            >
              <X size={12} aria-hidden />
              <span>Clear</span>
            </button>
          )}
          <span
            className="font-mono"
            style={{ fontSize: 11, color: overLimit ? 'var(--err)' : 'var(--muted-2)', fontWeight: overLimit ? 600 : 400 }}
          >
            {inputText.length.toLocaleString()} / {charLimit.toLocaleString()}
          </span>
          {overLimit && !isPro && onLimitAction && (
            <button
              type="button"
              onClick={onLimitAction}
              className="text-[11px] font-semibold underline underline-offset-2 cursor-pointer hover:opacity-80 transition-opacity"
              style={{ color: 'var(--err)' }}
            >
              {isAuthed ? 'Upgrade for more' : 'Sign in for more'}
            </button>
          )}
        </div>
      </div>

      {tab === 'text' && (
        /*
         * `overflow-hidden` is the fix for the example chips painting on top of
         * the Paste / Attach row: the textarea's 120px floor plus a wrapped
         * chip block is taller than the 250px the pane gets on a phone, and
         * without a clip the excess bled over the footer. Now the textarea
         * shrinks, and the chips sit in their own non-wrapping scroller.
         */
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden px-[18px] pb-3">
          <label htmlFor="source-text" className="sr-only">Text to translate</label>
          <textarea
            id="source-text"
            ref={textareaRef}
            value={inputText}
            onChange={(e) => onInputText(e.target.value)}
            onPaste={(e) => {
              // The browser's default paste lands the caret (and the whole
              // textarea viewport) at the END of the pasted text — with a
              // big paste it reads as "the whole thing scrolled". Hold the
              // viewport where it was so you keep your bearings; the caret
              // still ends up after the inserted text as usual.
              const el = e.currentTarget
              const top = el.scrollTop
              requestAnimationFrame(() => {
                el.scrollTop = top
              })
            }}
            placeholder={
              sourceLang === 'he'
                ? 'הקלד כאן לתרגום...'
                : 'Type or paste anything, Hebrew or English. Or drop a file here.'
            }
            dir={sourceRtl ? 'rtl' : 'ltr'}
            lang={sourceLang}
            rows={8}
            autoFocus
            className="w-full flex-1 min-h-[64px] md:min-h-[120px] resize-none bg-transparent leading-[1.5] focus:outline-none focus-visible:outline-none pt-1.5 text-[19px] md:text-[17px]"
            style={{
              fontFamily: sourceRtl ? 'var(--font-hebrew)' : 'var(--font-sans)',
              color: 'var(--ink)',
            }}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault()
                runTranslate()
              }
            }}
          />

          {!inputText && !isTranslating && (
            <div className="shrink-0 mt-3 pt-3 border-t -mx-[18px] px-[18px]" style={{ borderColor: 'var(--line-hair)' }}>
              <div className="font-mono uppercase mb-1.5" style={{ fontSize: 10, letterSpacing: '0.1em', color: 'var(--muted-2)' }}>
                Try an example
              </div>
              {/* One non-wrapping row that scrolls sideways on a phone, so the
                * chip block has a fixed height and can never grow into the
                * footer. Desktop keeps the wrapping cluster. */}
              {/* Wrapping on the phone too: a sideways scroller reads as a
                * chip cut off at the screen edge (blind review, 375px). */}
              <div className="flex flex-wrap gap-1.5 md:overflow-visible">
                {EXAMPLE_PHRASES.map((phrase) => (
                  <button
                    key={phrase}
                    type="button"
                    onClick={() => onExampleClick(phrase)}
                    dir="auto"
                    className="shrink-0 h-10 md:h-[28px] whitespace-nowrap text-[13px] md:text-[12px] font-medium px-3 md:px-2.5 transition-colors duration-150 cursor-pointer hover:border-[var(--line-strong,var(--line))] hover:text-[var(--ink)] hover:bg-[var(--card-2)] shadow-2xs"
                    style={{ borderRadius: 6, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--muted)' }}
                  >
                    {phrase}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'file' && isTranslating && fileJob ? (
        <div className="flex-1 min-h-0 overflow-y-auto px-[18px] pb-3">
          <FileTranslationProgressView
            job={fileJob}
            startedAt={translationStartMs}
            fact={hebrewFacts?.[factIndex % (hebrewFacts.length || 1)]}
            onCancel={onCancelFileJob}
          />
        </div>
      ) : tab === 'file' ? (
        <div className="px-[18px] pb-3 flex-1 min-h-0 overflow-y-auto">
          {file ? (
            <AttachedFilePreview file={file} previewUrl={filePreviewUrl} pageCount={pdfPageCount} />
          ) : restoredMedia ? (
            <div className="flex flex-col items-center gap-3">
              {restoredMedia.imageUrl ? (
                <img src={restoredMedia.imageUrl} alt="Original" className="w-full rounded-md" style={{ maxHeight: 320, objectFit: 'contain', background: 'var(--card-2)' }} />
              ) : (
                <div className="w-full min-h-[180px] flex items-center justify-center rounded-md border" style={{ borderColor: 'var(--line)', background: 'var(--card)' }}>
                  <DocumentBadge label={restoredMedia.kind === 'pdf' ? 'PDF' : 'DOC'} />
                </div>
              )}
              <span className="font-mono text-[11px] self-start" style={{ color: 'var(--muted-2)' }}>
                {restoredMedia.label} · from your history
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {/*
        * One footer for BOTH tabs. It used to render only for `tab === 'text'`,
        * which is why an uploaded file had no Translate button anywhere on
        * desktop: you could drop a PDF in and the page offered you nothing to
        * press. The primary action now sits in the same place whatever you are
        * translating.
        */}
      <div className="ws-source-footer flex items-center gap-2 px-[18px] py-3 md:py-3.5 border-t shrink-0 min-w-0 overflow-hidden" style={{ borderColor: 'var(--line-hair)' }}>
        {tab === 'text' && !inputText && !isTranslating && (
          <WsButtonSecondary icon={<ClipboardPaste size={16} aria-hidden />} onClick={onPasteFromClipboard} h={null} className="h-11 md:h-10 px-3 md:px-4 text-[14px]">
            Paste
          </WsButtonSecondary>
        )}
        <input
          ref={attachInputRef}
          type="file"
          className="sr-only"
          accept={FILE_ACCEPT}
          onChange={(e) => {
            const f = e.target.files?.[0] || null
            if (f) onAttach(f)
            e.target.value = ''
          }}
        />
        {!isTranslating && (
          <WsButtonSecondary icon={<Paperclip size={16} aria-hidden />} onClick={() => attachInputRef.current?.click()} h={null} className="h-11 md:h-10 px-3 md:px-4 text-[14px]">
            {tab === 'file' && file ? 'Replace' : 'Attach'}
          </WsButtonSecondary>
        )}

        <div className="flex-1" />

        {/*
          * Clear earns its own bordered button rather than the ghost it used to
          * be. Terracotta stays reserved for the primary action and selection,
          * so presence comes from the border, the surface and a 44px touch
          * target instead of the accent colour. It is a plain button, so it is
          * in the tab order and takes Enter and Space like any other.
          */}
        {hasSomethingToClear && !isTranslating && (
          <WsButtonSecondary
            icon={<X size={14} className="shrink-0" aria-hidden />}
            onClick={clearAndRefocus}
            h={null}
            className="h-11 md:h-10 px-4 text-[14px] font-semibold border-[var(--line-strong,var(--line))] hover:border-red-400 dark:hover:border-red-600 hover:bg-red-50/60 dark:hover:bg-red-950/25 hover:text-red-600 dark:hover:text-red-400 shadow-xs active:scale-[0.98] transition-colors duration-150 cursor-pointer"
            title="Clear (⌘ N or Esc)"
          >
            <span>Clear</span>
          </WsButtonSecondary>
        )}

        {/* Desktop only: on a phone the pinned bottom bar is the one and only
          * primary action, so this footer keeps just Paste / Attach / Clear. */}
        {isTranslating ? (
          <WsButtonPrimary onClick={onCancelTranslate} className="hidden md:inline-flex h-11 px-6 text-[15px] font-semibold tracking-tight">
            <Loader2 size={14} className="animate-spin" aria-hidden />
            Stop
          </WsButtonPrimary>
        ) : !(restoredMedia && !file) ? (
          <WsButtonPrimary
            onClick={() => runTranslate()}
            disabled={!canTranslate}
            kbd="⌘↵"
            className={cn(
              'hidden md:inline-flex h-11 px-6 text-[15px] font-semibold tracking-tight',
              canTranslate
                ? 'shadow-md shadow-brand-500/25'
                : 'opacity-40 cursor-not-allowed',
            )}
            title="Translate (⌘ Enter)"
          >
            {primaryLabel}
          </WsButtonPrimary>
        ) : null}
      </div>
    </div>
  )
}
